/**
 * SPRINT-035: Client secret storage and retrieval.
 *
 * SECURITY INVARIANTS:
 * - Secret is stored and used EXACTLY as issued by backend (no recomposition)
 * - Format: "{sessionId}.{rawSecret}" (UUID + '.' + 32-char base64url)
 * - Stored in sessionStorage (not localStorage) — cleared on tab close
 * - URL fragment cleared immediately after reading (prevents referrer leakage)
 * - Secret NEVER passed to analytics, logging, or non-auth headers
 */

const SECRET_PREFIX = 'checkout_secret_';
const REDIRECT_PREFIX = 'checkout_redirect_';

/**
 * Get client secret for a session.
 *
 * Priority:
 * 1. URL fragment `#cs={fullSecret}` — initial load from merchant redirect
 * 2. sessionStorage — return flow, page refresh
 *
 * Returns null if no secret is available (user must restart checkout).
 */
export function getClientSecret(sessionId: string): string | null {
  if (typeof window === 'undefined') return null;

  // 1. URL fragment (initial load)
  const hash = window.location.hash;
  if (hash.startsWith('#cs=')) {
    const fullSecret = decodeURIComponent(hash.slice(4));

    // Validate: secret must start with the sessionId
    if (!fullSecret.startsWith(`${sessionId}.`)) {
      console.error('[checkout] client_secret sessionId mismatch — ignoring fragment');
      return null;
    }

    // Persist for subsequent navigations (redirect return, refresh)
    sessionStorage.setItem(`${SECRET_PREFIX}${sessionId}`, fullSecret);

    // Clear fragment from URL (security — prevent referrer leakage)
    window.history.replaceState(null, '', window.location.pathname + window.location.search);

    return fullSecret;
  }

  // 2. sessionStorage (return flow or refresh)
  return sessionStorage.getItem(`${SECRET_PREFIX}${sessionId}`);
}

/**
 * Clear stored secret. Call on successful payment or explicit cleanup.
 */
export function clearClientSecret(sessionId: string): void {
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem(`${SECRET_PREFIX}${sessionId}`);
  }
}

/**
 * Store redirect info so we can recover if user refreshes during provider redirect.
 */
export function storeRedirectInfo(sessionId: string, attemptId: string, url: string): void {
  if (typeof window !== 'undefined') {
    sessionStorage.setItem(
      `${REDIRECT_PREFIX}${sessionId}`,
      JSON.stringify({ attemptId, url }),
    );
  }
}

/**
 * Get stored redirect info (e.g., on the /return page).
 */
export function getStoredRedirectInfo(
  sessionId: string,
): { attemptId: string; url: string } | null {
  if (typeof window === 'undefined') return null;
  const raw = sessionStorage.getItem(`${REDIRECT_PREFIX}${sessionId}`);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Clear redirect info after recovery.
 */
export function clearRedirectInfo(sessionId: string): void {
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem(`${REDIRECT_PREFIX}${sessionId}`);
  }
}
